import React, { useState, useEffect, useCallback, FormEvent } from 'react';
import { adminApi } from '../api/admin';
import { AccessCode, SiteStatus, Collection } from '../types';

type Tab = 'codes' | 'timer';

const ADMIN_SESSION_KEY = 'adminSession';
const SESSION_DURATION_MS = 60 * 60 * 1000; // 1 hour

// Helper to check if cached session is valid
function getCachedAdminSession(): string | null {
  const cached = localStorage.getItem(ADMIN_SESSION_KEY);
  if (!cached) return null;

  try {
    const { password, expiresAt } = JSON.parse(cached);
    if (Date.now() < expiresAt) {
      return password;
    }
    // Expired - clear it
    localStorage.removeItem(ADMIN_SESSION_KEY);
  } catch {
    localStorage.removeItem(ADMIN_SESSION_KEY);
  }
  return null;
}

// Helper to save admin session
function saveAdminSession(password: string): void {
  localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify({
    password,
    expiresAt: Date.now() + SESSION_DURATION_MS,
  }));
}

// Helper to clear admin session
function clearAdminSession(): void {
  localStorage.removeItem(ADMIN_SESSION_KEY);
}

// Helper to parse collection handles JSON
function parseCollectionHandles(json: string | null): string[] {
  if (!json) return ['vip-products'];
  try {
    return JSON.parse(json);
  } catch {
    return ['vip-products'];
  }
}

export function AdminPage() {
  // Admin auth state - check for cached session on init
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(true); // Start true to check cache

  // Tab state
  const [activeTab, setActiveTab] = useState<Tab>('codes');

  // Codes state
  const [codes, setCodes] = useState<AccessCode[]>([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [codesError, setCodesError] = useState('');

  // Available Shopify collections
  const [availableCollections, setAvailableCollections] = useState<Collection[]>([]);

  // Timer state
  const [siteStatus, setSiteStatus] = useState<SiteStatus | null>(null);
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [timerLoading, setTimerLoading] = useState(false);
  const [timerError, setTimerError] = useState('');

  // Code modal state (for both create and edit)
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [editingCode, setEditingCode] = useState<AccessCode | null>(null); // null = creating new
  const [modalCustomTitle, setModalCustomTitle] = useState('');
  const [modalSelectedCollections, setModalSelectedCollections] = useState<string[]>(['vip-products']);
  const [modalDefaultCollection, setModalDefaultCollection] = useState<string | null>(null);
  const [modalDiscountName, setModalDiscountName] = useState('');
  const [modalDiscountPercent, setModalDiscountPercent] = useState(5);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  // Load codes and config helper
  const loadCodesAndConfig = async (password: string) => {
    try {
      const [codesData, configData, collectionsData] = await Promise.all([
        adminApi.getCodes(password),
        adminApi.getConfig(password),
        adminApi.getShopifyCollections(password).catch(() => []),
      ]);
      setCodes(codesData);
      setSiteStatus(configData.status);
      setAvailableCollections(collectionsData);
    } catch (err: any) {
      console.error('Failed to load initial data:', err);
    }
  };

  // Check for cached admin session on mount
  useEffect(() => {
    const cachedPassword = getCachedAdminSession();
    if (cachedPassword) {
      // Verify the cached password is still valid
      adminApi.login(cachedPassword)
        .then(async (success) => {
          if (success) {
            setAdminPassword(cachedPassword);
            setIsAdminAuthenticated(true);
            // Load codes and config after restoring session
            await loadCodesAndConfig(cachedPassword);
          } else {
            clearAdminSession();
          }
        })
        .catch(() => {
          clearAdminSession();
        })
        .finally(() => {
          setLoginLoading(false);
        });
    } else {
      setLoginLoading(false);
    }
  }, []);

  // Admin login
  const handleAdminLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const success = await adminApi.login(adminPassword);
      if (success) {
        saveAdminSession(adminPassword);
        setIsAdminAuthenticated(true);
        // Load codes and config after login
        await loadCodesAndConfig(adminPassword);
      } else {
        setLoginError('Invalid password');
      }
    } catch (err: any) {
      setLoginError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    clearAdminSession();
    setIsAdminAuthenticated(false);
    setAdminPassword('');
  };

  // Load access codes
  const loadCodes = async () => {
    setCodesLoading(true);
    setCodesError('');

    try {
      const data = await adminApi.getCodes(adminPassword);
      setCodes(data);
    } catch (err: any) {
      setCodesError(err.message || 'Failed to load codes');
    } finally {
      setCodesLoading(false);
    }
  };

  // Load site config
  const loadConfig = useCallback(async () => {
    try {
      const data = await adminApi.getConfig(adminPassword);
      setSiteStatus(data.status);
    } catch (err: any) {
      setTimerError(err.message || 'Failed to load config');
    }
  }, [adminPassword]);

  // Delete code
  const handleDeleteCode = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this access code?')) {
      return;
    }

    try {
      await adminApi.deleteCode(adminPassword, id);
      loadCodes();
    } catch (err: any) {
      setCodesError(err.message || 'Failed to delete code');
    }
  };

  // Toggle code active
  const handleToggleCode = async (id: number) => {
    try {
      await adminApi.toggleCode(adminPassword, id);
      loadCodes();
    } catch (err: any) {
      setCodesError(err.message || 'Failed to toggle code');
    }
  };

  // Open modal for creating a new code
  const handleOpenCreateModal = () => {
    setEditingCode(null);
    setModalCustomTitle('');
    setModalSelectedCollections(['vip-products']);
    setModalDefaultCollection(null);
    setModalDiscountName('');
    setModalDiscountPercent(5);
    setModalError('');
    setShowCodeModal(true);
  };

  // Open modal for editing an existing code
  const handleOpenEditModal = (code: AccessCode) => {
    setEditingCode(code);
    setModalCustomTitle(code.custom_title || '');
    setModalSelectedCollections(parseCollectionHandles(code.collection_handles));
    setModalDefaultCollection(code.default_collection || null);
    setModalDiscountName(code.discount_name || '');
    setModalDiscountPercent(code.discount_percent || 5);
    setModalError('');
    setShowCodeModal(true);
  };

  // Close code modal
  const handleCloseModal = () => {
    setShowCodeModal(false);
    setEditingCode(null);
    setModalError('');
  };

  // Save (create or update) code
  const handleSaveCode = async (e: FormEvent) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError('');

    try {
      if (editingCode) {
        // Update existing code
        await adminApi.updateCode(adminPassword, editingCode.id, {
          customTitle: modalCustomTitle.trim() || undefined,
          collectionHandles: modalSelectedCollections.length > 0 ? modalSelectedCollections : ['vip-products'],
          defaultCollection: modalDefaultCollection,
          discountName: modalDiscountName.trim() || null,
          discountPercent: modalDiscountPercent,
        });
      } else {
        // Create new code
        await adminApi.createCode(adminPassword, {
          customTitle: modalCustomTitle.trim() || undefined,
          collectionHandles: modalSelectedCollections.length > 0 ? modalSelectedCollections : ['vip-products'],
          defaultCollection: modalDefaultCollection,
          discountName: modalDiscountName.trim() || null,
          discountPercent: modalDiscountPercent,
        });
      }
      setShowCodeModal(false);
      setEditingCode(null);
      loadCodes();
    } catch (err: any) {
      setModalError(err.response?.data?.error || err.message || (editingCode ? 'Failed to update code' : 'Failed to create code'));
    } finally {
      setModalLoading(false);
    }
  };

  // Handle collection selection toggle in modal
  const handleModalCollectionToggle = (handle: string) => {
    setModalSelectedCollections((prev) => {
      if (prev.includes(handle)) {
        const newList = prev.filter((h) => h !== handle);
        // If removing the default collection, clear it
        if (modalDefaultCollection === handle) {
          setModalDefaultCollection(newList.length > 0 ? null : null);
        }
        return newList.length > 0 ? newList : ['vip-products'];
      } else {
        return [...prev, handle];
      }
    });
  };

  // Start timer
  const handleStartTimer = async () => {
    setTimerError('');
    setTimerLoading(true);

    try {
      await adminApi.startCountdown(adminPassword, durationMinutes);
      loadConfig();
    } catch (err: any) {
      setTimerError(err.message || 'Failed to start timer');
    } finally {
      setTimerLoading(false);
    }
  };

  // Stop timer
  const handleStopTimer = async () => {
    if (!window.confirm('Are you sure you want to stop the countdown? This will deactivate the site.')) {
      return;
    }

    setTimerError('');
    setTimerLoading(true);

    try {
      await adminApi.stopCountdown(adminPassword, true);
      loadConfig();
    } catch (err: any) {
      setTimerError(err.message || 'Failed to stop timer');
    } finally {
      setTimerLoading(false);
    }
  };

  // Copy code to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Format time remaining
  const formatTimeRemaining = (seconds: number | null): string => {
    if (seconds === null || seconds <= 0) return 'Expired';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'Hour' : 'Hours'}`);
    if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'Minute' : 'Minutes'}`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs} ${secs === 1 ? 'Second' : 'Seconds'}`);

    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
    return parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
  };

  // Refresh timer status periodically
  useEffect(() => {
    if (!isAdminAuthenticated) return;

    const interval = setInterval(loadConfig, 10000);
    return () => clearInterval(interval);
  }, [isAdminAuthenticated, loadConfig]);

  // Admin login form
  if (!isAdminAuthenticated) {
    return (
      <div className="admin-page">
        <div className="admin-login">
          <h1>Admin Login</h1>
          <form onSubmit={handleAdminLogin}>
            <div className="form-group">
              <label htmlFor="adminPassword">Admin Password</label>
              <input
                type="password"
                id="adminPassword"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                disabled={loginLoading}
              />
            </div>
            {loginError && <div className="error-message">{loginError}</div>}
            <button type="submit" disabled={loginLoading}>
              {loginLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <h1>Admin Dashboard</h1>
        <button onClick={handleLogout} className="logout-btn">
          Logout
        </button>
      </header>

      <nav className="admin-tabs">
        <button
          className={activeTab === 'codes' ? 'active' : ''}
          onClick={() => setActiveTab('codes')}
        >
          Access Codes
        </button>
        <button
          className={activeTab === 'timer' ? 'active' : ''}
          onClick={() => setActiveTab('timer')}
        >
          Timer Control
        </button>
      </nav>

      <main className="admin-content">
        {/* Codes Tab */}
        {activeTab === 'codes' && (
          <div className="codes-tab">
            <div className="tab-header">
              <h2>Access Codes ({codes.length})</h2>
              <div className="tab-header-actions">
                <button onClick={handleOpenCreateModal} className="generate-btn">
                  Generate Code
                </button>
                <button onClick={loadCodes} disabled={codesLoading}>
                  {codesLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
            </div>

            {codesError && <div className="error-message">{codesError}</div>}

            <table className="codes-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Custom Title</th>
                  <th>Collections</th>
                  <th>Discount</th>
                  <th>Uses</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((code) => {
                  const collections = parseCollectionHandles(code.collection_handles);
                  return (
                    <tr key={code.id} className={code.is_active ? '' : 'inactive'}>
                      <td>
                        <code>{code.code}</code>
                        <button
                          className="copy-btn"
                          onClick={() => copyToClipboard(code.code)}
                          title="Copy code"
                        >
                          Copy
                        </button>
                      </td>
                      <td>{code.custom_title || '-'}</td>
                      <td>{collections.join(', ')}</td>
                      <td>
                        {code.discount_name || 'VIP Discount'} ({code.discount_percent}%)
                      </td>
                      <td>{code.use_count}</td>
                      <td>{code.is_active ? 'Active' : 'Inactive'}</td>
                      <td>
                        <button onClick={() => handleOpenEditModal(code)} className="edit-btn">
                          Edit
                        </button>
                        <button onClick={() => handleToggleCode(code.id)}>
                          {code.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button onClick={() => handleDeleteCode(code.id)} className="delete-btn">
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Timer Tab */}
        {activeTab === 'timer' && (
          <div className="timer-tab">
            <h2>Timer Control</h2>

            {timerError && <div className="error-message">{timerError}</div>}

            <div className="timer-status">
              <h3>Current Status</h3>
              <p>
                <strong>Site Active:</strong>{' '}
                {siteStatus?.isSiteActive ? 'Yes' : 'No'}
              </p>
              <p>
                <strong>Time Remaining:</strong>{' '}
                {formatTimeRemaining(siteStatus?.timeRemaining ?? null)}
              </p>
              {siteStatus?.countdownEndTime && (
                <p>
                  <strong>Ends At:</strong>{' '}
                  {new Date(siteStatus.countdownEndTime).toLocaleString()}
                </p>
              )}
            </div>

            <div className="timer-controls">
              <h3>Start New Countdown</h3>
              <div className="form-group">
                <label htmlFor="duration">Duration (minutes)</label>
                <input
                  type="number"
                  id="duration"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(parseInt(e.target.value) || 0)}
                  min={1}
                  disabled={timerLoading}
                />
              </div>
              <div className="duration-presets">
                <button onClick={() => setDurationMinutes(30)}>30 min</button>
                <button onClick={() => setDurationMinutes(60)}>1 hour</button>
                <button onClick={() => setDurationMinutes(120)}>2 hours</button>
                <button onClick={() => setDurationMinutes(1440)}>24 hours</button>
                <button onClick={() => setDurationMinutes(10080)}>7 days</button>
              </div>
              <div className="timer-actions">
                <button
                  onClick={handleStartTimer}
                  disabled={timerLoading || durationMinutes <= 0}
                  className="start-btn"
                >
                  {timerLoading ? 'Starting...' : 'Start Countdown'}
                </button>
                <button
                  onClick={handleStopTimer}
                  disabled={timerLoading || !siteStatus?.isSiteActive}
                  className="stop-btn"
                >
                  Stop & Deactivate
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Code Modal (Create/Edit) */}
      {showCodeModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingCode ? `Edit Access Code: ${editingCode.code}` : 'Generate Access Code'}</h2>

            {modalError && <div className="error-message">{modalError}</div>}

            <form onSubmit={handleSaveCode}>
              <div className="form-group">
                <label htmlFor="modalCustomTitle">Custom Page Title (optional)</label>
                <input
                  type="text"
                  id="modalCustomTitle"
                  value={modalCustomTitle}
                  onChange={(e) => setModalCustomTitle(e.target.value)}
                  placeholder="e.g., Acme Corp VIP Access"
                  disabled={modalLoading}
                />
              </div>

              <div className="form-group">
                <label>Product Collections</label>
                <p className="field-hint">Select which Shopify collections this code can access:</p>
                <div className="collection-checkboxes">
                  {availableCollections.length > 0 ? (
                    availableCollections.map((col) => (
                      <label key={col.handle} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={modalSelectedCollections.includes(col.handle)}
                          onChange={() => handleModalCollectionToggle(col.handle)}
                          disabled={modalLoading}
                        />
                        {col.title} ({col.handle})
                        {col.productCount !== undefined && ` - ${col.productCount} products`}
                      </label>
                    ))
                  ) : (
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={modalSelectedCollections.includes('vip-products')}
                        onChange={() => handleModalCollectionToggle('vip-products')}
                        disabled={modalLoading}
                      />
                      vip-products (default)
                    </label>
                  )}
                </div>
              </div>

              {modalSelectedCollections.length > 1 && (
                <div className="form-group">
                  <label htmlFor="modalDefaultCollection">Default Collection View</label>
                  <select
                    id="modalDefaultCollection"
                    value={modalDefaultCollection || ''}
                    onChange={(e) => setModalDefaultCollection(e.target.value || null)}
                    disabled={modalLoading}
                  >
                    <option value="">Show All Products</option>
                    {modalSelectedCollections.map((handle) => {
                      const col = availableCollections.find((c) => c.handle === handle);
                      return (
                        <option key={handle} value={handle}>
                          {col?.title || handle}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="modalDiscountName">Discount Name (optional)</label>
                <input
                  type="text"
                  id="modalDiscountName"
                  value={modalDiscountName}
                  onChange={(e) => setModalDiscountName(e.target.value)}
                  placeholder="e.g., VIP Discount (default)"
                  disabled={modalLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="modalDiscountPercent">Discount Percentage</label>
                <input
                  type="number"
                  id="modalDiscountPercent"
                  value={modalDiscountPercent}
                  onChange={(e) => setModalDiscountPercent(parseInt(e.target.value) || 0)}
                  min={0}
                  max={100}
                  disabled={modalLoading}
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={handleCloseModal} disabled={modalLoading}>
                  Cancel
                </button>
                <button type="submit" disabled={modalLoading} className="save-btn">
                  {modalLoading ? (editingCode ? 'Saving...' : 'Generating...') : (editingCode ? 'Save Changes' : 'Generate Code')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
